/**
 * Created by nicolas.looschen@pikobytes.de on 04.12.2023.
 *
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.txt', which is part of this source code package.
 */

'use client';

import { getOptions, languages } from 'configuration/i18nSettings';
import i18next, { type Namespace } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import resourcesToBackend from 'i18next-resources-to-backend';
import { useEffect, useState } from 'react';
import {
    initReactI18next,
    useTranslation as useTranslationOrg,
} from 'react-i18next';
import { addCustomFormats } from './addCustomFormats';

const runsOnServerSide = typeof window === 'undefined';

//
i18next
    .use(initReactI18next)
    .use(LanguageDetector)
    .use(
        resourcesToBackend(
            (language: string, namespace: string) =>
                import(
                    `../../../locales/${namespace}/${namespace}.${language}.json`
                )
        )
    )
    .init({
        ...getOptions(),
        lng: undefined, // let detect the language on client side
        detection: {
            order: ['path', 'htmlTag', 'navigator'],
        },
        preload: runsOnServerSide ? languages : [],
    });

addCustomFormats(i18next);

export function useClientSideTranslation(
    lng: string,
    ns: Namespace,
    options: { keyPrefix?: string } = {}
) {
    const ret = useTranslationOrg(ns, options);
    const { i18n } = ret;
    if (runsOnServerSide && lng && i18n.resolvedLanguage !== lng) {
        i18n.changeLanguage(lng);
    } else {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [activeLng, setActiveLng] = useState(i18n.resolvedLanguage);
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
            if (activeLng === i18n.resolvedLanguage) return;
            setActiveLng(i18n.resolvedLanguage);
        }, [activeLng, i18n.resolvedLanguage]);
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
            if (!lng || i18n.resolvedLanguage === lng) return;
            i18n.changeLanguage(lng);
        }, [lng, i18n]);
    }
    return ret;
}
